import { preloadImage } from '@theme/utilities';

const LENS_SIZE = 200; // px — keep in sync with --lens-size in product-media-gallery-content-styles.liquid
const ZOOM_FACTOR = 2.25;

const canUseLens = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/** @type {WeakSet<Element>} */
const initializedContainers = new WeakSet();

/** @type {Set<string>} */
const preloadedSources = new Set();

/**
 * Wires up a cursor-following magnifying lens for a single product image slide.
 * Each slide (`.product-media-container--image`) owns its own <img>, so binding
 * per-slide means the lens always reflects whichever image is currently visible —
 * no explicit "current image" tracking is needed when a thumbnail or arrow changes
 * the active slide.
 *
 * @param {HTMLElement} container - A `.product-media-container--image` element.
 */
function setUpLens(container) {
  if (initializedContainers.has(container)) return;
  initializedContainers.add(container);

  // Neutralize Horizon's built-in zoom-dialog trigger for this slide so the lens
  // fully replaces it, even if the block's "Enable zoom" setting is left on.
  container.removeAttribute('on:click');
  container.classList.remove('product-media-container--zoomable');
  container.querySelector('.product-media-container__zoom-button')?.remove();

  /** @type {HTMLDivElement | null} */
  let lens = null;

  const getImage = () => container.querySelector('img.product-media__image');

  const ensureLens = () => {
    if (lens) return lens;
    lens = document.createElement('div');
    lens.className = 'product-image-lens';
    lens.setAttribute('aria-hidden', 'true');
    container.appendChild(lens);
    return lens;
  };

  const removeLens = () => {
    lens?.remove();
    lens = null;
  };

  /** @param {PointerEvent} event */
  const updateLens = (event) => {
    if (!canUseLens()) return removeLens();

    const image = getImage();
    if (!(image instanceof HTMLImageElement) || !image.naturalWidth) return;

    const imageRect = image.getBoundingClientRect();
    const x = event.clientX - imageRect.left;
    const y = event.clientY - imageRect.top;

    // Outside the rendered image (e.g. letterboxed padding) — hide the lens.
    if (x < 0 || y < 0 || x > imageRect.width || y > imageRect.height) {
      removeLens();
      return;
    }

    const source = image.getAttribute('data_max_resolution') || image.currentSrc || image.src;
    if (source && !preloadedSources.has(source)) {
      preloadedSources.add(source);
      preloadImage(source);
    }

    const currentLens = ensureLens();
    const half = LENS_SIZE / 2;

    const clampedX = Math.min(Math.max(x, half), imageRect.width - half);
    const clampedY = Math.min(Math.max(y, half), imageRect.height - half);

    const containerRect = container.getBoundingClientRect();
    const offsetX = imageRect.left - containerRect.left;
    const offsetY = imageRect.top - containerRect.top;

    currentLens.style.transform = `translate(${offsetX + clampedX - half}px, ${offsetY + clampedY - half}px)`;
    currentLens.style.backgroundImage = `url("${source}")`;
    currentLens.style.backgroundSize = `${imageRect.width * ZOOM_FACTOR}px ${imageRect.height * ZOOM_FACTOR}px`;
    currentLens.style.backgroundPosition = `-${clampedX * ZOOM_FACTOR - half}px -${clampedY * ZOOM_FACTOR - half}px`;
    currentLens.setAttribute('data-visible', 'true');
  };

  container.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    updateLens(event);
  });

  container.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    removeLens();
  });
}

function initAll() {
  if (!canUseLens()) return;

  document.querySelectorAll('.product-media-container--image').forEach((container) => {
    if (container instanceof HTMLElement) setUpLens(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-scan after the Theme Editor swaps in new section/block markup.
document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:block:select', initAll);
